import * as Api from 'src/api'
import { StudentDegreeEncoder, type AdministratorProgram, type StudentDegree } from 'src/api/types'
import type { FormValues } from './types'

export type ProgramsSaveBody = Parameters<typeof Api.postAdministratorProgramsUpdate>[0]['body']

export function majorMinorFlags(hasMajor: boolean, hasMinor: boolean): number {
  if (hasMajor && hasMinor) return 3
  if (hasMajor) return 1
  if (hasMinor) return 2

  return 0
}

function splitMajorMinor(hasMajorMinor: number | undefined): { hasMajor: boolean; hasMinor: boolean } {
  const v = hasMajorMinor ?? 0

  return {
    hasMajor: v === 1 || v === 3,
    hasMinor: v === 2 || v === 3,
  }
}

export function programToFormValues(program: AdministratorProgram | null | undefined): FormValues {
  const { hasMajor, hasMinor } = splitMajorMinor(program?.hasMajorMinor)

  return {
    name: program?.name ?? '',
    nameEn: program?.nameEn ?? '',
    fullname: program?.fullname ?? '',
    fullnameEn: program?.fullnameEn ?? '',
    minorFullname: program?.minorFullname ?? '',
    minorFullnameEn: program?.minorFullnameEn ?? '',
    direction: program?.direction ?? '',
    directionEn: program?.directionEn ?? '',
    specialty: program?.specialty ?? '',
    specialtyEn: program?.specialtyEn ?? '',
    faculty: program?.facultyId?.toString() ?? '',
    facultySchoolId: program?.facultySchoolId?.toString() ?? '',
    prefix: program?.prefix ?? '',
    code: program?.code ?? '',
    degree: program?.degree != null ? String(StudentDegreeEncoder(program.degree.value)) : '',
    status: program?.status?.toString() ?? '',
    hasMajor,
    hasMinor,
    allowInRegistration: program?.allowInRegistration === 'true',
    allowInMobility: program?.allowInMobility === 'true',
    testType: program?.testtypeId?.toString() ?? '',
    failsForExpel: program?.failsForExpel === 1,
    registrationRequirements: program?.registrationRequirements ?? '',
    hasFreeCredits: program?.hasFreeCredits ?? false,
    languageRestrictionEnabled: program?.languageRestrictionEnabled ?? false,
    showFreeCreditCourses: program?.showFreeCreditCourses ?? false,
    genCredits: program?.genCredits ?? 0,
    baseCredits: program?.baseCredits ?? 0,
    otherCredits: program?.otherCredits ?? 0,
    description: program?.description ?? '',
    descriptionEn: program?.descriptionEn ?? '',
    minorDescription: program?.minorDescription ?? '',
    minorDescriptionEn: program?.minorDescriptionEn ?? '',
    requirementsMajor: program?.requirementsMajor ?? '',
    requirementsMajorEn: program?.requirementsMajorEn ?? '',
    requirementsMinor: program?.requirementsMinor ?? '',
    requirementsMinorEn: program?.requirementsMinorEn ?? '',
    qualificationMajor: program?.qualificationMajor ?? '',
    qualificationMajorEn: program?.qualificationMajorEn ?? '',
    qualificationFields: program?.qualificationFields ?? '',
    qualificationFieldsEn: program?.qualificationFieldsEn ?? '',
    price: program?.price ?? 0,
    creditYears: program?.creditYears ?? 0,
    learningLang: program?.learningLang ?? '',
    qualificationCredits: program?.qualificationCredits ?? 0,
    minorGenCredits: program?.minorGenCredits ?? 0,
    minorBaseCredits: program?.minorBaseCredits ?? 0,
    minorOtherCredits: program?.minorOtherCredits ?? 0,
  }
}

function degreeFormValueToNumber(degree: string): number {
  const trimmed = degree.trim()
  const asNum = Number(trimmed)

  if (trimmed !== '' && !Number.isNaN(asNum)) {
    return asNum
  }

  if (trimmed === '') {
    return 0
  }

  return Number(StudentDegreeEncoder(trimmed as StudentDegree))
}

export function valuesToSaveBody(values: FormValues): ProgramsSaveBody {
  return {
    name: values.name.trim(),
    nameEn: values.nameEn.trim(),
    fullname: values.fullname.trim(),
    fullnameEn: values.fullnameEn.trim(),
    minorFullname: values.minorFullname.trim(),
    minorFullnameEn: values.minorFullnameEn.trim(),
    direction: values.direction.trim(),
    directionEn: values.directionEn.trim(),
    specialty: values.specialty.trim(),
    specialtyEn: values.specialtyEn.trim(),
    degree: degreeFormValueToNumber(values.degree),
    facultyId: Number(values.faculty),
    status: Number(values.status),
    code: values.code.trim(),
    prefix: values.prefix.trim(),
    testtypeId: values.testType ? Number(values.testType) : null,
    allowInRegistration: values.allowInRegistration,
    allowInMobility: values.allowInMobility,
    hasMajorMinor: majorMinorFlags(values.hasMajor, values.hasMinor),
    facultySchoolId: values.facultySchoolId ? Number(values.facultySchoolId) : null,
    failsForExpel: values.failsForExpel ? 1 : 0,
    registrationRequirements: values.registrationRequirements,
    hasFreeCredits: values.hasFreeCredits,
    languageRestrictionEnabled: values.languageRestrictionEnabled,
    showFreeCreditCourses: values.showFreeCreditCourses,
    genCredits: values.genCredits,
    baseCredits: values.baseCredits,
    otherCredits: values.otherCredits,
    price: values.price,
    creditYears: values.creditYears.toString(),
    learningLang: values.learningLang.trim(),
    qualificationCredits: values.qualificationCredits,
    minorGenCredits: values.minorGenCredits,
    minorBaseCredits: values.minorBaseCredits,
    minorOtherCredits: values.minorOtherCredits,
    description: values.description,
    descriptionEn: values.descriptionEn,
    requirementsMajor: values.requirementsMajor,
    requirementsMajorEn: values.requirementsMajorEn,
    requirementsMinor: values.requirementsMinor,
    requirementsMinorEn: values.requirementsMinorEn,
    qualificationMajor: values.qualificationMajor,
    qualificationMajorEn: values.qualificationMajorEn,
    qualificationFields: values.qualificationFields,
    qualificationFieldsEn: values.qualificationFieldsEn,
    minorDescription: values.minorDescription,
    minorDescriptionEn: values.minorDescriptionEn,
  }
}
